import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { noop } from "lodash";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as React from "react";
import { connect } from "react-redux";

import { ITransactionAttachment } from "interfaces/entities/transaction";
import { getItem, removeItem, saveItem } from "services/session-storage";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { TRANSACTION, TransactionActions } from "store/entities/transaction/actions";
import {
  ICreateTransactionFailurePayload,
  IMergeTransactionFailurePayload,
  IMergeTransactionSuccessPayload,
  ITransactionFetchSuccessPayload,
  ITransactionUploadAttachmentFailurePayload,
  ITransactionUploadAttachmentSuccessPayload,
  TransactionType,
} from "store/entities/transaction/interfaces";
import { selectTransactions } from "store/entities/transaction/selectors";
import { IAction, IActionWithPayload } from "store/helper";

export interface IUploadContext {
  uploadAttachment: (
    localID: string,
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attachment: any,
    onProgress: (event: ProgressEvent) => void,
    fileName?: string,
    accept?: string[]
  ) => void;
  deleteAttachment: (attachmentId: string) => void;
  mergeTransaction: (mergeParams: IMergeParams, onFail?: () => void) => void;
  abortUpload: (localID: string) => void;
  reset: () => void;
  attachmentsSize: () => number;
  isUploading: () => boolean;
  openFileDialog: (options: IFileDialogOptions) => void;
  transactionID?: string;
  attachments: ITransactionAttachment[];
  enabled: boolean;
}

export const AttachmentContext = React.createContext<IUploadContext>({
  uploadAttachment: noop,
  deleteAttachment: noop,
  mergeTransaction: noop,
  openFileDialog: noop,
  abortUpload: noop,
  reset: noop,
  attachmentsSize: () => 0,
  isUploading: () => false,
  attachments: [],
  enabled: false,
});

interface ITransactionParams {
  transactionTicketID?: string;
  transactionAgentID?: string;
  transactionCannedResponseID?: string;
  transactionEphemeralID?: string;
  transactionID?: string;
}

interface IMergeParams {
  agentID?: string;
  cannedResponseID?: string;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: any) => ({
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  uploadAttachment: (
    localID: string,
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attachment: any,
    onProgress: (event: ProgressEvent) => void,
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    xhrCallback: (xhr: XMLHttpRequest, body: any) => void,
    identifier?: { ticketID?: string; agentID?: string; cannedResponseID: string },
    transactionID?: string,
    fileName?: string,
    accept?: string[],
    transactionType?: TransactionType
  ) =>
    dispatch(
      TransactionActions.uploadAttachment({
        localID,
        ...identifier,
        transactionID,
        onProgress,
        attachment,
        fileName,
        xhrCallback,
        accept,
        transactionType,
      })
    ),

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mergeTransaction: (transactionParams: ITransactionParams, mergeParams: IMergeParams) =>
    dispatch(TransactionActions.mergeTransaction({ ...transactionParams, ...mergeParams })),

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteAttachment: (attachmentID: string, transactionID: string) =>
    dispatch(TransactionActions.deleteAttachment({ attachmentID, transactionID })),

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchTransaction: (transactionID: string) => dispatch(TransactionActions.fetch(transactionID)),
});

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => ({
  transactions: selectTransactions(state),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface IProps {
  ticketID?: string;
  agentID?: string;
  cannedResponseID?: string;
  ephemeralID?: string;
  transactionType?: TransactionType;
  onTransactionChange?: (transactionID: string) => void;
  children?: React.ReactNode;
}

interface IFileDialogOptions {
  multiple?: boolean;
  accept?: string;
  onFileSelect: (files: File[]) => void;
}

export const AttachmentProvider: React.FC<IProps & StateProps & DispatchProps> = ({
  uploadAttachment,
  deleteAttachment,
  mergeTransaction,
  fetchTransaction,
  ticketID,
  agentID,
  ephemeralID,
  children,
  transactions,
  cannedResponseID,
  onTransactionChange,
  transactionType,
}) => {
  // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
  const [transactionID, setTransactionID] = useState<string>(null);
  const uploadMap = useRef<{ [localID: string]: { xhr: XMLHttpRequest & { aborted?: boolean }; file: File } }>({});
  const uploadInProgressCounter = useRef<number>(0);
  const [mergeLock, setMergeLock] = useState(false);
  const mergeRemoveTask = useRef<[number | null, () => void, string[]]>([null, noop, []]);

  const [uploadInProgress, setUploadInProgress] = useState<number>(0);

  const singleFileInput = useRef<HTMLInputElement | null>(null);
  const multipleFileInput = useRef<HTMLInputElement | null>(null);
  const fileChangeHandler = useRef<(files: File[]) => void>(noop);

  useEffect(() => {
    const savedTransactionID =
      ticketID || ephemeralID ? getItem(`attachmentTransactionId_${ticketID || ephemeralID}`) : null;
    if (savedTransactionID) {
      setTransactionID(savedTransactionID);
      if (typeof onTransactionChange === "function") {
        onTransactionChange(savedTransactionID);
      }
      fetchTransaction(savedTransactionID);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      filter(
        (action: IActionWithPayload<string, ITransactionUploadAttachmentSuccessPayload>) =>
          action.type === TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.SUCCESS]
      ),
      subscribe((action: IActionWithPayload<string, ITransactionUploadAttachmentSuccessPayload>) => {
        const { transactionID: newTransactionID } = action.payload;

        if (!transactionID) {
          setTransactionID(newTransactionID);
          saveItem(`attachmentTransactionId_${ticketID || ephemeralID}`, newTransactionID);

          if (typeof onTransactionChange === "function") {
            onTransactionChange(newTransactionID);
          }
        }
      })
    );

    return unsubscribe;
  }, [ephemeralID, onTransactionChange, ticketID, transactionID]);

  const reset = useCallback(() => {
    removeItem(`attachmentTransactionId_${ticketID || ephemeralID}`);
    if (transactionID) {
      removeItem(`${transactionID}_inlineImageAttachments`);
    }

    // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    setTransactionID(null);

    if (typeof onTransactionChange === "function") {
      // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      onTransactionChange(null);
    }
  }, [ephemeralID, onTransactionChange, ticketID, transactionID]);

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction<string>) => action.type === TRANSACTION.FETCH_SINGLE[RequestAction.FAILURE]),
      subscribe((action: IActionWithPayload<string, ITransactionFetchSuccessPayload>) => {
        const { id } = action.payload;

        if (id === transactionID) {
          reset();
        }
      })
    );

    return unsubscribe;
  });

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction<string>) =>
        [
          TRANSACTION.MERGE_TRANSACTION[RequestAction.SUCCESS],
          TRANSACTION.MERGE_TRANSACTION[RequestAction.FAILURE],
        ].includes(action.type)
      ),
      subscribe(
        (
          action:
            | IActionWithPayload<string, IMergeTransactionFailurePayload>
            | IActionWithPayload<string, IMergeTransactionSuccessPayload>
        ) => {
          const {
            transactionID: mergeTransactionID,
            ephemeralID: mergeEphemeralID,
            ticketID: mergeTicketID,
            agentID: mergeAgentID,
          } = action.payload;

          if (
            !transactionID &&
            ephemeralID === mergeEphemeralID &&
            ticketID === mergeTicketID &&
            agentID === mergeAgentID
          ) {
            saveItem(`attachmentTransactionId_${ticketID || ephemeralID}`, mergeTransactionID);
            setTransactionID(mergeTransactionID);
            if (typeof onTransactionChange === "function") {
              onTransactionChange(mergeTransactionID);
            }
          }

          setMergeLock(false);
        }
      )
    );

    return unsubscribe;
  });

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction<string>) =>
        [
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.FAILURE],
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.SUCCESS],
          TRANSACTION.CREATE[RequestAction.FAILURE],
        ].includes(action.type)
      ),
      subscribe(
        (
          action:
            | IActionWithPayload<string, ITransactionUploadAttachmentSuccessPayload>
            | IActionWithPayload<string, ITransactionUploadAttachmentFailurePayload>
            | IActionWithPayload<string, ICreateTransactionFailurePayload>
        ) => {
          if (action.type === TRANSACTION.CREATE[RequestAction.FAILURE]) {
            // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
            setTransactionID(null);
            uploadInProgressCounter.current = 0;
            setUploadInProgress(uploadInProgressCounter.current);
          } else {
            const { localID } = action.payload as
              | ITransactionUploadAttachmentSuccessPayload
              | ITransactionUploadAttachmentFailurePayload;

            delete uploadMap.current[localID];
            uploadInProgressCounter.current -= 1;
            setUploadInProgress(uploadInProgressCounter.current);
          }
        }
      )
    );

    return unsubscribe;
  }, [uploadInProgress]);

  const upload = useCallback(
    (
      localID: string,
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      attachment: any,
      onProgress: (event: ProgressEvent) => void,
      fileName?: string,
      accept?: string[]
    ) => {
      uploadAttachment(
        localID,
        attachment,
        onProgress,
        (xhr, body) => {
          uploadMap.current[localID] = { xhr, file: body.get("attachments") as File };
        },
        // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        { ticketID, agentID, cannedResponseID },
        transactionID,
        fileName,
        accept,
        transactionType
      );
      uploadInProgressCounter.current += 1;
      setUploadInProgress(uploadInProgressCounter.current);
    },
    [agentID, ticketID, transactionID, uploadAttachment, transactionType, cannedResponseID]
  );

  const remove = useCallback(
    (attachmentID: string) => {
      if (transactionID) {
        if (mergeLock) {
          mergeRemoveTask.current[2] = [...mergeRemoveTask.current[2], attachmentID];
        } else {
          deleteAttachment(attachmentID, transactionID);
        }
      }
    },

    [deleteAttachment, transactionID, mergeLock]
  );

  const merge = useCallback(
    (mergeParams: IMergeParams) => {
      const transactionParams = {
        transactionID,
        transactionAgentID: agentID,
        transactionCannedResponseID: cannedResponseID,
        transactionTicketID: ticketID,
        transactionEphemeralID: ephemeralID,
      };

      setMergeLock(true);
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      mergeRemoveTask.current[1] = function removeTaskPoolFunction() {
        if (mergeLock) {
          mergeRemoveTask.current[0] = setTimeout(mergeRemoveTask.current[1], 100) as unknown as number;
        } else {
          // @ts-expect-error TS(2769): No overload matches this call.
          clearTimeout(mergeRemoveTask.current[0]);
          mergeRemoveTask.current[0] = null;
          mergeRemoveTask.current[2].forEach(attachmentID => {
            deleteAttachment(attachmentID, transactionID);
          });
          mergeRemoveTask.current[2] = [];
        }
      };
      mergeRemoveTask.current[0] = setTimeout(mergeRemoveTask.current[1], 100) as unknown as number;
      mergeTransaction(transactionParams, mergeParams);
    },
    [transactionID, agentID, ticketID, ephemeralID, mergeTransaction, mergeLock, deleteAttachment, cannedResponseID]
  );

  const abort = useCallback((localID: string) => {
    if (uploadMap.current[localID]) {
      uploadMap.current[localID].xhr.aborted = true;
      uploadMap.current[localID].xhr.abort();
    }
  }, []);

  const restoreDefaultInputStates = useCallback(() => {
    if (singleFileInput.current) {
      // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
      singleFileInput.current.accept = undefined;
    }

    if (multipleFileInput.current) {
      // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
      multipleFileInput.current.accept = undefined;
    }
  }, []);

  const openFileDialog = useCallback(
    (options: IFileDialogOptions) => {
      restoreDefaultInputStates();
      const defaultOptions: IFileDialogOptions = {
        multiple: false,
        accept: undefined,
        onFileSelect: noop,
      };
      const { multiple, accept, onFileSelect } = { ...defaultOptions, ...options };
      const targetInputRef = multiple ? multipleFileInput.current : singleFileInput.current;

      if (targetInputRef) {
        if (accept) {
          targetInputRef.accept = accept;
        }

        fileChangeHandler.current = onFileSelect;
        targetInputRef.click();
      }
    },
    [restoreDefaultInputStates]
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileInputChange = useCallback((event: any) => {
    const { files } = event.target;
    fileChangeHandler.current(files);
  }, []);

  const transaction = transactionID ? transactions[transactionID] : null;

  return (
    <AttachmentContext.Provider
      value={{
        uploadAttachment: upload,
        deleteAttachment: remove,
        abortUpload: abort,
        reset,
        mergeTransaction: merge,
        enabled: true,
        attachmentsSize: () =>
          Object.values(uploadMap.current).reduce((t, a) => t + a.file.size, 0) +
          (transaction ? transaction.attachments.reduce((t, a) => t + a.size, 0) : 0),
        isUploading: () => uploadInProgress > 0 || mergeLock,
        openFileDialog,
        attachments: transaction ? transaction.attachments : [],
        transactionID,
      }}
    >
      <input
        type="file"
        multiple
        name="provider-multiple-file-input"
        style={{ display: "none" }}
        ref={multipleFileInput}
        value=""
        onChange={handleFileInputChange}
        data-testid="multiple-file-input"
      />
      <input
        type="file"
        name="provider-single-file-input"
        style={{ display: "none" }}
        ref={singleFileInput}
        value=""
        onChange={handleFileInputChange}
        data-testid="single-file-input"
      />

      {children}
    </AttachmentContext.Provider>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentProvider);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAttachments = () => {
  const context = useContext(AttachmentContext);
  return context;
};
